/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import * as appStrings from '@touchplan/strings';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { OrganizationService } from '../../services/organization.service';

@Component({
	selector: 'organization-app-info',
	templateUrl: './organization-app-info.component.html',
	styleUrls: ['./organization-app-info.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationAppInfoComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public isAdding: boolean;
	@Input() public parentFormGroup: UntypedFormGroup;
	public appStrings = appStrings;
	public readonly formGroupName = 'appOrgInfoFormGroup';
	public appOrgInfoForm: UntypedFormGroup = this.formBuilder.group({
		name: ['', Validators.required],
		description: [''],
		url: ['', Validators.required],
		clientID: [],
	});
	private shutdownSubject$ = new Subject<void>();
	constructor(private organizationService: OrganizationService, private formBuilder: UntypedFormBuilder) {}

	public ngOnInit(): void {
		// Note that "appOrgInfoForm" is added to "parentFormGroup" by "ngOnChanges"
		// instead of here, since "ngOnChanges" is notified of the initial "set" of the "parentFormGroup"
		// value, and that function needs to handle all kinds of "parentFormGroup" anyway.

		combineLatest([this.organizationService.applicationUser$, this.organizationService.application$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([appUser, appOrg]) => {
				if (appOrg) {
					this.appOrgInfoForm.setValue(
						{
							name: appOrg.view.name,
							description: appOrg.view.description,
							url: appOrg.view.url,
							clientID: appUser ? appUser.$id : '',
						},
						{ emitEvent: false }
					);
				} else {
					// If editor state isAdding, clear and reset form
					if (this.isAdding) {
						this.appOrgInfoForm.reset();
					}
				}
			});

		// Update the data view fields when the corresponding UI text fields change, and are valid:
		for (const name of ['name', 'description', 'url']) {
			const formControl = this.appOrgInfoForm.get(name);
			formControl.valueChanges
				.pipe(
					filter(() => formControl.valid),
					takeUntil(this.shutdownSubject$)
				)
				.subscribe((newValue) => {
					this.organizationService.updateApplication(name, newValue);
				});
		}
	}

	// Takes care of connecting this component's FormGroup to the @Input parentFormGroup,
	// AND changing that connection if necessary (if the @Input value changes):
	public ngOnChanges(changes: SimpleChanges): void {
		const parentFormGroupChange = changes['parentFormGroup'];
		if (parentFormGroupChange) {
			const newParentFormGroup = parentFormGroupChange.currentValue as UntypedFormGroup;
			if (!newParentFormGroup) {
				// Parent is gone !  Remove from the old one:
				(this.appOrgInfoForm.parent as UntypedFormGroup).removeControl(this.formGroupName);
			} else if (parentFormGroupChange.isFirstChange || this.appOrgInfoForm.parent !== newParentFormGroup) {
				// Adding to parent for the first time, or the parent has changed:
				this.parentFormGroup.addControl(this.formGroupName, this.appOrgInfoForm);
			}
		}
	}

	public openSecretDialog() {
		this.organizationService.generateSecret();
	}

	public ngOnDestroy(): void {
		this.parentFormGroup.removeControl(this.formGroupName);
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
